import { DestroyRef, Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import moment from 'moment';
import { combineLatest, EMPTY, forkJoin, iif, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { AddressModel } from '../../models/complex/address.model';
import {
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from '../../models/complex/time-recording-entry.model';
import { TimeRecordingLimitedEntryModel } from '../../models/complex/time-recording-limited-entry.model';
import { TimesheetAndExpensesP58Model } from '../../models/complex/timesheet-and-expenses-p58.model';
import { TimesheetEntryInterface } from '../../models/complex/timesheet-entry.interface';
import { TimesheetEntryModel } from '../../models/complex/timesheet-entry.model';
import { TimesheetEowModel } from '../../models/complex/timesheet-eow.model';
import { TimesheetGapModel } from '../../models/complex/timesheet-gap.model';
import { TimesheetManualEntryModel } from '../../models/complex/timesheet-manual-entry.model';
import { TimesheetSowModel } from '../../models/complex/timesheet-sow.model';
import { ExpenseModel } from '../../models/low-level/expense.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import {
    TimesheetNotificationModel,
    TimesheetNotificationModelFromJson,
} from '../../models/low-level/timesheet-notification.model';
import { TimesheetModel, TimesheetModelFromJson } from '../../models/low-level/timesheet.model';
import { TextModel } from '../../models/primitive/text.model';
import { TimesheetAndExpensesUtilities } from '../../utilities/timesheet-and-expenses-utilities';
import { ConfigService } from '../config/config.service';
import { StorageAddressService } from './storage-address.service';
import { OdataTimesheetService } from './odata-timesheet.service';
import { UserMasterService } from '../user-master/user-master.service';
import { StorageTimesheetService } from './storage-timesheet.service';
import { EventService } from '../event/event.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TIMESHEET_KEYS } from '../../enums/timesheet.enum';
import { CONCURRENT_USAGE_ERROR_CODES } from '../../enums/concurrent-usage.enum';
import { TimesheetAddressModel } from '../../models/complex/timesheet-address.model';
import { isDefined } from '../../utilities/is-defined';
import { NotificationStatusService } from '../notification-status/notification-status.service';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

@Injectable({
    providedIn: 'root',
})
export class TimesheetAndExpensesService implements InitializableInterface {
    public isInitialized = false;

    private addresses: AddressModel[] = [];
    private currentTimesheet$: Observable<TimesheetModel>;
    private submittedTimesheet$: Observable<TimesheetModel>;

    private currentTimesheetMeta = {
        saveTimestamps: [] as number[],
        isConfirmationSubmitted: {} as Record<string, boolean>,
    };

    public constructor(
        private readonly configService: ConfigService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageTimesheetService: StorageAddressService,
        private readonly storageTimesheetService: StorageTimesheetService,
        private readonly odataTimesheetService: OdataTimesheetService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        private readonly userMasterService: UserMasterService,
        private readonly eventService: EventService,
        private readonly notificationStatusService: NotificationStatusService,
        private readonly destroyRef: DestroyRef,
    ) {
        this.currentTimesheet$ = this.storageTimesheetService.getTimesheet$(TIMESHEET_KEYS.CURRENT_TIMESHEET).pipe(
            filter(isDefined),
            tap((timesheet) => {
                this.addAddressFromExistingManualTimesheetEntries(timesheet.manualTimesheetEntries);
                this.updateCurrentTimesheetMeta(timesheet);
            }),
        );
        this.submittedTimesheet$ = this.storageTimesheetService
            .getTimesheet$(TIMESHEET_KEYS.SUBMITTED_TIMESHEET)
            .pipe(filter(isDefined));

        this.eventService.synchronizationRequested$
            .pipe(
                filter(() => this.isInitialized),
                mergeMap(() => combineLatest([this.currentTimesheet$, this.submittedTimesheet$]).pipe(take(1))),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    public init(): Observable<void> {
        this.log.debug(`TimesheetAndExpensesService: init: init the timesheet and expenses service (${Date.now()})`);
        return this.refreshAddresses().pipe(
            mergeMap(() => this.storageTimesheetService.getTimesheet$(TIMESHEET_KEYS.CURRENT_TIMESHEET).pipe(take(1))),
            mergeMap((timesheet) =>
                iif(
                    () => isDefined(timesheet),
                    of(timesheet),
                    this.saveCurrentTimesheet$(new TimesheetModel(moment.invalid(), [], [], [], false)),
                ),
            ),
            mergeMap(() =>
                this.storageTimesheetService.getTimesheet$(TIMESHEET_KEYS.SUBMITTED_TIMESHEET).pipe(take(1)),
            ),
            mergeMap((timesheet) =>
                iif(
                    () => isDefined(timesheet),
                    of(timesheet),
                    this.saveSubmittedTimesheet$(new TimesheetModel(moment.invalid(), [], [], [], false)),
                ),
            ),
            tap(() => (this.isInitialized = true)),
            map(() => undefined),
        );
    }

    public refreshAddresses(): Observable<AddressModel[]> {
        return this.getAddressesFromLocalStorage().pipe(
            tap((addressSet) => {
                this.addresses = addressSet;
            }),
        );
    }

    /**
     * This method converts the frontend model timesheetAndExpenses to the model used for business logic and GW communication: currentTimesheet.
     *
     * @private
     * @param {TimesheetAndExpensesP58Model} timesheetAndExpenses - model used specifically on the timesheetAndExpenses component for forms and validation.
     * @param {TimesheetModel} currentTimesheet - the timesheet model to modify and prepare for saving.
     * @returns TimesheetModel | void: currentTimesheet
     * @throws Error on initialization error, or inconsistencies between frontend model and business logic model.
     */
    private prepareTimesheetFormModelForSave(
        timesheetAndExpenses: TimesheetAndExpensesP58Model,
        currentTimesheet: TimesheetModel,
    ): TimesheetModel | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: autoSave: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        // no model -> no saving
        if (!timesheetAndExpenses) {
            return undefined;
        }

        // SHCNGMP58-977-Timesheet-Problem
        // Fix: Prevent overwriting of the Start Of Work when timesheet is in readonly mode
        // if the timesheet is in readonly mode than return as there will be no data to save
        if (timesheetAndExpenses.readonly) {
            return undefined;
        }

        // when there are no entries in the timesheet, we also don't need to save anything
        if (!timesheetAndExpenses.timesheetEntries.value[0]) {
            return undefined;
        }

        // create a clone without calling cloneDeep
        const newCurrentTimesheet = new TimesheetModelFromJson(
            JSON.parse(JSON.stringify(currentTimesheet)),
            TIMESHEET_KEYS.CURRENT_TIMESHEET,
        );

        // Save the start of work and end of work values
        const startOfWorkEntry = <TimesheetSowModel>(
            timesheetAndExpenses.timesheetEntries.value.find((entry) => TimesheetAndExpensesService.isSowEntry(entry))
        );
        if (startOfWorkEntry && startOfWorkEntry.startDate.value !== newCurrentTimesheet.startOfWork) {
            newCurrentTimesheet.startOfWork = startOfWorkEntry.startDate.value;
        }
        const endOfWorkEntry = <TimesheetEowModel>(
            timesheetAndExpenses.timesheetEntries.value.find((entry) => TimesheetAndExpensesService.isEowEntry(entry))
        );
        if (endOfWorkEntry && endOfWorkEntry.endDate.value !== newCurrentTimesheet.endOfWork) {
            newCurrentTimesheet.endOfWork = endOfWorkEntry.endDate.value;
        }

        if (this.configService.getDisplayTimesheetAddressOptions()) {
            // save start and end addresses from timesheet
            newCurrentTimesheet.startAddressValue = startOfWorkEntry.startAddressOptions.selectedEntry.model.value;
            if (endOfWorkEntry) {
                newCurrentTimesheet.endAddressValue = endOfWorkEntry.endAddressOptions.selectedEntry.model.value;
            }
        }
        const travelBackEntries = <TimesheetEntryModel[]>(
            timesheetAndExpenses.timesheetEntries.value.filter((entry) =>
                TimesheetAndExpensesService.isTravelBackEntry(entry),
            )
        );
        travelBackEntries.forEach((entry) => {
            // Get matching timesheet notification model (with its corresponding entries)
            const timesheetNotification = newCurrentTimesheet.notifications.find((timesheetNotificationModel) =>
                timesheetNotificationModel.notificationId.equalsTo(entry.notificationId),
            );

            if (!timesheetNotification) {
                throw new Error(
                    `TimesheetAndExpensesService: autoSave: Could not find timesheet notification for notification ${entry.notificationId.toString()} (from timesheet entry ${
                        entry.workCode
                    })`,
                );
            }

            // Find matching travel back entry in saved timesheet
            const timesheetTravelBack = timesheetNotification.timesheetEntries.find((timesheetEntry) =>
                TimesheetAndExpensesService.isTravelBackEntry(timesheetEntry),
            );

            if (!timesheetTravelBack) {
                throw new Error(
                    `TimesheetAndExpensesService: prepareTimesheetForSave: Could not find travel back entry in entries of timesheet notification for notification ${timesheetNotification.notificationId.toString()}`,
                );
            }

            // Sanity check: Make sure both entries start at the same time
            if (!entry.startTime.value.isSame(timesheetTravelBack.startTime.value)) {
                this.log.error(
                    'TimesheetAndExpensesService: prepareTimesheetForSave: Unable to find matching travel back slot in timesheet',
                );
                return;
            }

            // Check if the travel back time was modified
            if (!entry.endTime.value.isSame(timesheetTravelBack.endTime.value)) {
                // If yes, save modified time in timesheet notification model
                timesheetTravelBack.endTime.value.set(entry.endTime.value.toObject());
            }
        });
        // save manual entries
        newCurrentTimesheet.manualTimesheetEntries = <TimesheetManualEntryModel[]>(
            timesheetAndExpenses.timesheetEntries.value.filter((entry) =>
                TimesheetAndExpensesService.isManualEntry(entry),
            )
        );

        // save expenses added/deleted to notifications and additional, also the distance added only for additional expenses
        _.forOwn(timesheetAndExpenses.expensesDictionary, (expensesArrayModel, notificationIdString) => {
            if (expensesArrayModel && expensesArrayModel.value) {
                const id = NotificationIdentifierModel.fromString(notificationIdString);
                this.saveNotificationExpenses(
                    id,
                    expensesArrayModel.value,
                    id.qmNum ? new TextModel('Distance') : timesheetAndExpenses.additionalDistance,
                    newCurrentTimesheet,
                );
            }
        });

        return newCurrentTimesheet;
    }

    public selectCurrentTimesheet$(): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: selectCurrentTimesheet$: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        return this.currentTimesheet$;
    }

    public saveCurrentTimesheet$(timesheet: TimesheetModel): Observable<TimesheetModel> {
        this.log.debug(
            `TimesheetAndExpensesService: saveCurrentTimesheet: save a timesheet to UserProfileSet and IndexedDb. (${Date.now()})`,
        );

        return this.storageTimesheetService.saveDistinctTimesheet$(TIMESHEET_KEYS.CURRENT_TIMESHEET, timesheet);
    }

    /**
     * This method is saving the current timesheet state from the timesheetAndExpenses component to concurrent store and IndexedDb.
     *
     * @param {TimesheetAndExpensesP58Model} timesheetAndExpenses
     * @returns TimesheetModel
     */
    public saveTimesheetFormModel(
        timesheetAndExpenses: TimesheetAndExpensesP58Model,
    ): Observable<TimesheetModel | void> {
        this.log.debug(
            `TimesheetAndExpensesService: saveAndUploadTimesheet: save timesheet to user profile set and indexeddb (${Date.now()})`,
        );
        return this.selectCurrentTimesheet$().pipe(
            take(1),
            mergeMap((timesheet) => {
                if (timesheet.isRemoteTimesheet) {
                    return throwError(() => CONCURRENT_USAGE_ERROR_CODES.TIMESHEET_OUTDATED);
                }
                // If the current timesheet's start of work is not valid, there is a chance that the UI model did not update yet and is trying
                // writing the UI model into the current timesheet. This is the source of a difficult race condition bug from Jan 2026,
                // that made reproducing the problem very difficult, because it would happen only sometimes.
                // FIX: So checking here, if the start of work is valid on the current timesheet.
                if (!timesheet.startOfWork.isValid()) {
                    timesheetAndExpenses.readonly = true;
                    return EMPTY;
                }
                return of(timesheet);
            }),
            map((currentTimesheet) => this.prepareTimesheetFormModelForSave(timesheetAndExpenses, currentTimesheet)),
            mergeMap((preparedTimesheet: TimesheetModel) => {
                if (!isDefined(preparedTimesheet)) {
                    return of(undefined);
                }
                this.checkIsConfirmationSubmittedFlagsInCurrentTimesheetChanged(preparedTimesheet);
                return this.saveCurrentTimesheet$(preparedTimesheet);
            }),
        );
    }

    public submit(model: TimesheetAndExpensesP58Model): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: submit: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        return this.odataTimesheetService.saveTimesheet(model).pipe(
            mergeMap(() => this.selectCurrentTimesheet$().pipe(take(1))),
            tap((timesheet) => {
                this.eventService.triggerChangesOnSubmittingTimesheet(timesheet);
            }),
            mergeMap((timesheet) =>
                forkJoin([this.saveSubmittedTimesheet$(timesheet), this.setNewEmptyCurrentTimesheet()]),
            ),
            // Mark the timesheet and expenses P58 model as read only to prevent writing back to the timesheet on indexeddb.
            tap(() => (model.readonly = true)),
            mergeMap(() => {
                // start sync
                this.log.debug('TimesheetAndExpensesService: submit: sendOfflineData');
                return this.synchronizationService.sendOfflineData();
            }),
        );
    }

    public selectSubmittedTimesheet$(): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: selectSubmittedTimesheet: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        return this.submittedTimesheet$;
    }

    private saveSubmittedTimesheet$(timesheet: TimesheetModel): Observable<TimesheetModel> {
        return this.storageTimesheetService.saveDistinctTimesheet$(TIMESHEET_KEYS.SUBMITTED_TIMESHEET, timesheet);
    }

    public getStartOfWork$(): Observable<moment.Moment> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getStartOfWork: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        return this.selectCurrentTimesheet$().pipe(map((timesheet) => timesheet.startOfWork));
    }

    public saveStartOfWork(newStartOfWork: moment.Moment): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveStartOfWork: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        return this.selectCurrentTimesheet$().pipe(
            take(1),
            mergeMap((timesheet: Readonly<TimesheetModel>) => {
                if (!timesheet) {
                    throw new Error(`TimesheetAndExpensesService: saveStartOfWork: currentTimesheet is undefined`);
                }
                if (timesheet.startOfWork.valueOf() !== newStartOfWork.valueOf()) {
                    const newCurrentTimesheet = { ...timesheet, startOfWork: newStartOfWork };
                    return this.saveCurrentTimesheet$(newCurrentTimesheet);
                }
                return of(timesheet);
            }),
        );
    }

    public deleteSubmittedNotificationInfoFromTimesheet(
        timesheet: Readonly<TimesheetModel>,
    ): Observable<TimesheetModel> {
        return of(timesheet).pipe(
            map((ts) => {
                const notifications = timesheet.notifications.filter(
                    (notification: TimesheetNotificationModelFromJson) => !notification.isConfirmationSubmitted,
                );
                return { ...ts, notifications: notifications || [] };
            }),
            mergeMap((ts) => this.saveCurrentTimesheet$(ts)),
        );
    }

    /**
     * This method is to retrieve timesheet related information (expenses, distance etc) for a notification saved as a TimesheetNotificationModel
     *
     * @param notificationId the notification identifier
     * @param timesheet the timesheet to search in
     * @returns the TimesheetNotificationModel or undefined if not found
     * @throws Error if the service is not initialized
     */
    public getTimesheetNotification(
        notificationId: NotificationIdentifierModel,
        timesheet: TimesheetModel,
    ): TimesheetNotificationModel | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getTimesheetNotification: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        return timesheet.notifications.find(
            (n) => n.isConfirmationSubmitted && n.notificationId.equalsTo(notificationId),
        );
    }

    /**
     * This method is to save all timesheet related information (distance, expenses, time-recording) for a notification coming from confirmation screen to the local store
     *
     * @param notification the notification to save
     * @param expenses the expenses to save
     * @param timeRecordingEntries the time-recording entries to save
     * @param distance the distance to save
     * @param isConfirmationSubmitted
     * @param isAfterHoursCallOut
     * @returns the saved TimesheetModel or undefined if not found
     * @throws Error if the service is not initialized
     */
    public saveTimesheetNotification(
        notification: NotificationModel,
        expenses: ExpenseModel[],
        timeRecordingEntries: TimeRecordingEntryModel[],
        distance: TextModel,
        isAfterHoursCallOut = false,
    ): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveTimesheetNotification: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        this.log.debug(
            `TimesheetAndExpensesService: saveTimesheetNotification: save timesheet notification from confirmation "${notification.identifier.toString()}" (${Date.now()})`,
        );
        const enableStandbyTravel = this.configService.getEnableStandbyTravel();

        // transform TimeRecordingEntryModel[] to TimesheetEntryModel[]
        let isDistanceAdded = false;
        const timesheetEntryModelArray = timeRecordingEntries.map((timeRecordingEntry) => {
            let workCode = timeRecordingEntry.getTypeOfWorkCode();
            const activityType = '';
            let meansOfTransport = '';

            // For NGMP58, along with TRTO and TRBK, we use TRTU (unpaid travel to) and TRBU (unpaid travel back)
            if (
                timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                this.configService.getDisplayMeansOfTransport()
            ) {
                if (timeRecordingEntry.meansOfTransport) {
                    meansOfTransport = timeRecordingEntry.meansOfTransport.selectedEntry?.value;
                }
            }

            // If standby travel enabled, then default travel-back entry must be standby
            if (
                enableStandbyTravel &&
                timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                timeRecordingEntry.typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
            ) {
                workCode = TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY;
            }

            // add distance to TRTO timerecording entry.
            if (
                !isDistanceAdded &&
                timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                timeRecordingEntry.typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO
            ) {
                isDistanceAdded = true;
                return new TimesheetEntryModel(
                    notification.identifier,
                    timeRecordingEntry.startDateTime,
                    timeRecordingEntry.endDateTime,
                    workCode,
                    activityType,
                    notification.address.getTimesheetAddress(),
                    distance,
                    meansOfTransport,
                    false,
                );
            }

            return new TimesheetEntryModel(
                notification.identifier,
                timeRecordingEntry.startDateTime,
                timeRecordingEntry.endDateTime,
                workCode,
                activityType,
                notification.address.getTimesheetAddress(),
                undefined,
                meansOfTransport,
                enableStandbyTravel &&
                    timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                    this.configService.getIsTravelBillable(notification.orderType),
                isAfterHoursCallOut,
            );
        });

        // When modifying the timesheet from a different component (e.g. confirmation) make sure that newest version of timesheet is loaded from concurrent store.
        return this.storageTimesheetService.getTimesheet$(TIMESHEET_KEYS.CURRENT_TIMESHEET).pipe(
            withLatestFrom(
                this.notificationStatusService
                    .isNotificationCompleted(notification.identifier)
                    // don't interrupt the pipe, if the notification completed status errors.
                    .pipe(catchError(() => of(false))),
            ),
            take(1),
            mergeMap(([timesheet, isNotificationCompleted]) => {
                if (!isDistanceAdded) {
                    this.log.warn(
                        `TimesheetAndExpensesService: saveTimesheetNotification: No time recording entry exists for TRTO for notification ${notification.identifier.toString()}, so distance cannot be added to notification. `,
                    );
                }

                // create a clone without calling cloneDeep
                const newCurrentTimesheet = new TimesheetModelFromJson(
                    JSON.parse(JSON.stringify(timesheet)),
                    TIMESHEET_KEYS.CURRENT_TIMESHEET,
                );

                let timesheetNotification = newCurrentTimesheet.notifications.find((currentNotificationModel) => {
                    return currentNotificationModel.notificationId.equalsTo(notification.identifier);
                });
                if (!timesheetNotification) {
                    timesheetNotification = new TimesheetNotificationModel(
                        notification.identifier,
                        [],
                        [],
                        new TextModel('Distance'),
                        notification.address.completeName,
                        isNotificationCompleted,
                    );
                    newCurrentTimesheet.notifications.push(timesheetNotification);
                }
                timesheetNotification.expenses = expenses;
                timesheetNotification.distance = distance;
                timesheetNotification.timesheetEntries = timesheetEntryModelArray;
                timesheetNotification.isConfirmationSubmitted = isNotificationCompleted;
                // sort notifications of currentTimesheet by startDate before saving
                TimesheetAndExpensesUtilities.sortNotificationsByFirstTimesheetEntryStartTime(newCurrentTimesheet);
                return this.storageTimesheetService.saveDistinctTimesheet$(
                    TIMESHEET_KEYS.CURRENT_TIMESHEET,
                    newCurrentTimesheet,
                );
            }),
        );
    }

    public getAddresses(): AddressModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        return this.addresses;
    }

    private getAddressesFromLocalStorage(): Observable<AddressModel[]> {
        return this.localStorageTimesheetService
            .getAddresses()
            .pipe(map((addresses) => _.sortBy(addresses, 'addrNumber')));
    }

    public saveAddresses(addresses: AddressModel[]): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        if (this.configService.getEnableStatesOfUSADropdown()) {
            addresses.forEach((address) => AddressModel.updateStateOfUSA(address));
        }
        this.addresses = addresses;
        return this.localStorageTimesheetService.saveAddresses(addresses);
    }

    public addAddressesAndSave(newAddresses: AddressModel[]): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: addAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        let isChanged = false;

        newAddresses.forEach((newAddress) => {
            const newTimesheetAddress = newAddress.getTimesheetAddress();

            const existingAddrKeyIndex = this.addresses.findIndex(
                (address) => newTimesheetAddress.addrKey === address.getTimesheetAddress().addrKey,
            );
            const existingAddress = this.addresses.find((address) =>
                newAddress.getTimesheetAddress().equalsTo(address.getTimesheetAddress()),
            );

            if (existingAddrKeyIndex === -1 && !existingAddress) {
                // Address does not exist → ADD
                this.addresses.unshift(newAddress);
                isChanged = true;
            } else if (existingAddrKeyIndex !== -1) {
                // Address exists → UPDATE
                this.addresses[existingAddrKeyIndex] = newAddress;
                isChanged = true;
            }
        });

        if (isChanged) {
            return this.saveAddresses(this.addresses);
        }

        return of(undefined);
    }

    public resetAddresses(): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: resetAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        this.addresses.map((address) => (address.visible = true));
        return this.saveAddresses(this.addresses);
    }

    public hideAddress(address: AddressModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: hideAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        const index = this.addresses.findIndex((addr) => address.addrNumber === addr.addrNumber);
        this.addresses[index].visible = false;
        return this.saveAddresses(this.addresses);
    }

    /**
     * Removes an address from the local storage
     *
     * @param address the address to remove
     * @returns an observable that completes when the address is removed
     * @throws Error if the service is not initialized
     */
    public deleteAddress(address: AddressModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: deleteAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        _.remove(this.addresses, (addr) => address.getTimesheetAddress().equalsTo(addr.getTimesheetAddress()));
        return this.saveAddresses(this.addresses);
    }

    // type guard function for TimesheetGapModel
    public static isGapEntry(entry: TimesheetEntryInterface): entry is TimesheetGapModel {
        return entry instanceof TimesheetGapModel;
    }

    // type guard function for TimesheetManualEntryModel
    public static isManualEntry(entry: TimesheetEntryInterface): entry is TimesheetManualEntryModel {
        return entry instanceof TimesheetManualEntryModel;
    }

    // type guard function for TimesheetEntryModel
    public static isNormalEntry(entry: TimesheetEntryInterface): entry is TimesheetEntryModel {
        return entry instanceof TimesheetEntryModel;
    }

    // type guard function for TimesheetSoWModel
    public static isSowEntry(entry: TimesheetEntryInterface): entry is TimesheetSowModel {
        return entry instanceof TimesheetSowModel;
    }

    // type guard function for TimesheetEoWModel
    public static isEowEntry(entry: TimesheetEntryInterface): entry is TimesheetEowModel {
        return entry instanceof TimesheetEowModel;
    }

    public static isTravelBackEntry(entry: TimesheetEntryInterface): boolean {
        return (
            entry instanceof TimesheetEntryModel &&
            (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK ||
                entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY)
        );
    }

    public static isTravelToEntry(entry: TimesheetEntryInterface): boolean {
        return (
            entry instanceof TimesheetEntryModel &&
            (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO ||
                entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY)
        );
    }

    /**
     * This method is to save changes done to timesheet-expenses and also the additional distance value
     *
     * @param notificationId the notification identifier
     * @param notificationExpenses the expenses to save
     * @param distance the distance to save
     * @param timesheet
     * @returns the saved TimesheetModel
     */
    private saveNotificationExpenses(
        notificationId: NotificationIdentifierModel,
        notificationExpenses: ExpenseModel[],
        distance: TextModel,
        timesheet: TimesheetModel,
    ): void {
        this.log.debug(
            `TimesheetAndExpensesService: saveNotificationExpenses: save the notification expenses. (${Date.now()})`,
        );
        let timesheetNotification = timesheet.notifications.find((currentNotificationModel) =>
            currentNotificationModel.notificationId.equalsTo(notificationId),
        );
        if (!timesheetNotification) {
            timesheetNotification = new TimesheetNotificationModel(
                notificationId,
                [],
                [],
                new TextModel('Distance'),
                '',
                true,
            );
            timesheet.notifications.push(timesheetNotification);
        }
        // save distance only for additional expenses, distance for notifications are saved while saving confirmation
        if (!notificationId.qmNum) {
            timesheetNotification.distance = distance;
        }
        timesheetNotification.expenses = notificationExpenses;
    }

    private addAddressFromExistingManualTimesheetEntries(manualTimesheetEntries: TimesheetManualEntryModel[]): void {
        if (this.isInitialized) {
            this.addAddressesAndSave(
                manualTimesheetEntries
                    .map((manualTimesheet) => manualTimesheet?.address)
                    .filter((address): address is TimesheetAddressModel => !!address)
                    .map((address) => AddressModel.fromTimesheetAddress(address)),
            )
                .pipe(take(1))
                .subscribe();
        }
    }

    private setNewEmptyCurrentTimesheet(): Observable<TimesheetModel> {
        return this.storageTimesheetService.saveDistinctTimesheet$(
            TIMESHEET_KEYS.CURRENT_TIMESHEET,
            this.userMasterService.isPaceOnlineUser()
                ? new TimesheetModel(moment().startOf('day'), [], [], [], false)
                : new TimesheetModel(moment.invalid(), [], [], [], false),
        );
    }

    /**
     * Refresh the meta data information of the current timesheet.
     * Interesting values are:
     *  - saveTimestamp (timestamp of last save to concurrency store)
     *  - isConfirmationSubmitted flag of all notification on timesheet
     *
     * @param timesheet
     * @private
     */
    private updateCurrentTimesheetMeta(timesheet: TimesheetModel): void {
        this.updateTimesheetMetaData(this.currentTimesheetMeta.saveTimestamps, timesheet.saveTimestamp);
        this.updateIsConfirmationSubmittedFlagsInCurrentTimesheet(timesheet);
        this.log.debug(
            `TimesheetAndExpensesService: updateCurrentTimesheetMeta: \n` +
                ` update timestamp: from "${this.currentTimesheetMeta.saveTimestamps[this.currentTimesheetMeta.saveTimestamps.length - 2]}" to "${this.currentTimesheetMeta.saveTimestamps[this.currentTimesheetMeta.saveTimestamps.length - 1]}" \n`,
        );
        if (this.checkOverwriteWithOlderTimestamp(this.currentTimesheetMeta.saveTimestamps)) {
            const message = `TimesheetAndExpensesService: updateCurrentTimesheetMeta: overwriting with old data. Offending update ${this.currentTimesheetMeta.saveTimestamps[this.currentTimesheetMeta.saveTimestamps.length - 1]}`;
            const error = new Error(message);
            this.log.error(message + ` \n ${error.stack}`);
        }
    }

    /**
     * Compare the new timesheet's isConfirmationSubmitted status of all notifications with the previous values. Document when an erroneous update is done in the logs.
     *
     * @param newTimesheet incoming new timesheet
     * @private
     */
    private checkIsConfirmationSubmittedFlagsInCurrentTimesheetChanged(newTimesheet: TimesheetModel): void {
        // Additional Logs to debug inconsistencies in the currentTimesheet:
        newTimesheet.notifications.forEach((notif) => {
            const oldStatus = this.currentTimesheetMeta.isConfirmationSubmitted[notif.notificationId.toString()];
            const newStatus = notif.isConfirmationSubmitted;

            if (oldStatus && !newStatus) {
                const error = new Error();
                const message = `TimesheetAndExpensesService: saveAndUploadTimesheet: ERROR isConfirmationSubmitted flag for ${notif.notificationId.toString()} in service is being overwritten from ${oldStatus} to ${newStatus}. \n${error.stack}`;
                this.log.error(message);
            }
            if (oldStatus !== newStatus) {
                const message = `TimesheetAndExpensesService: saveAndUploadTimesheet: isConfirmationSubmitted flag for ${notif.notificationId.toString()} in service is being overwritten from ${oldStatus} to ${newStatus}.`;
                this.log.debug(message);
            }
        });
    }

    private updateIsConfirmationSubmittedFlagsInCurrentTimesheet(timesheet: TimesheetModel): void {
        this.currentTimesheetMeta.isConfirmationSubmitted = {};
        timesheet.notifications.forEach((notif) => {
            this.currentTimesheetMeta.isConfirmationSubmitted[notif.notificationId.toString()] =
                notif.isConfirmationSubmitted;
        });
    }

    private updateTimesheetMetaData<T>(list: T[], newElem: T): void {
        list.push(newElem);
        if (list.length > 100) {
            list.shift();
        }
    }
    private checkOverwriteWithOlderTimestamp(list: number[]): boolean {
        if (list.length < 2) {
            return false;
        }
        if (list[list.length - 1] < list[list.length - 2]) {
            this.log.error(
                `TimesheetAndExpensesService: checkOverwriteWithOlderTimestamp: currentTimesheet change version "${list[list.length - 2]}" overwritten with older version ${list[list.length - 1]}`,
            );
            return true;
        }
        return false;
    }
}
